/**
 * taxInvoiceReceipt — ใบกำกับภาษีเต็มรูป (ใบกำกับภาษี/ใบเสร็จรับเงิน) ขนาด A4
 *
 * กิน payload ของ GET /v1/bills/{id} ซึ่งมีทั้งค่าภาษีของร้าน (filter.shop_tax),
 * รหัสสถานประกอบการของสาขา และ snapshot `tax_invoice_*` ที่บันทึกไว้ตอนออกใบ
 * จึงไม่ต้องยิง /configuration เพิ่ม
 *
 * ⚠️ เคยมีตัวสร้าง "ใบกำกับเต็มรูปบนสลิป 80mm" อยู่ในไฟล์นี้ — ลบออกแล้ว (2026-09-11)
 *    ไม่เคยมีใครเรียกเลยตั้งแต่ commit แรกที่เพิ่มเข้ามา (89cf608, พ.ค. 2026) และขาดของ
 *    ที่ ม.86/4 บังคับถึง 5 อย่าง (ชื่อนิติบุคคล · ที่อยู่ผู้ขาย · ที่อยู่ผู้ซื้อ · ปริมาณ ·
 *    ข้อความ "ออกแทนใบกำกับภาษีอย่างย่อ") ทั้งยังบวกเลขในคอลัมน์ไม่ลงตัว
 *    ใบกำกับเต็มรูปของไทยใช้ A4 เท่านั้น — อย่าเอากลับมา
 */

import { groupItemsForReceipt, printPdf, formatBranchNo } from '@/utils/billReceipt';
import { bahtText } from '@/utils/bahtText';

function toNum(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * @param {object} opts
 * @param {object} opts.bill   GET /v1/bills/{id} `data` payload
 * @param {object} opts.shopTax filter.shop_tax — ภาษีเป็นของร้าน ไม่ใช่ของสาขา
 * @param {string} opts.brandName
 * @param {function} opts.fmt  currency formatter
 * @param {function} opts.t    i18n translator (default identity)
 */
/**
 * ลำดับบรรทัดสรุปยอดของ "ใบกำกับภาษีเต็มรูป" แบบราคารวมภาษี (VAT included)
 *
 * คืน array ของ { label, value, bold } ให้ผู้เรียกไปวาดตามหน้ากระดาษของตัวเอง
 * (A4 เป็นแถวตาราง · สลิปเป็น columns) — ตัวเลขและลำดับต้องเป็นชุดเดียวกันทั้งสองแบบ
 *
 * ⚠️ ลำดับนี้บัญชีเป็นคนกำหนด (2026-09-11) ห้ามสลับเอง
 *
 * ปัญหาเดิมที่แก้:
 *  1. **สลิปบวกเลขไม่ลงตัว** — โชว์ "ยอดรวม" ด้วยค่า `beforeVat` (ซึ่งรวมค่าบริการอยู่แล้ว)
 *     แล้วโชว์ค่าบริการเป็นอีกบรรทัดต่อท้าย → 1,079.44 + 105.00 + 75.56 = 1,260 ≠ 1,155
 *     ใครไล่บวกตามคอลัมน์จะได้คนละเลขกับยอดสุทธิ
 *  2. **A4 เรียงแล้วชวนเข้าใจผิด** — วางยอดสุทธิไว้ล่างสุดถัดจากมูลค่าก่อนภาษี + ภาษี
 *     เจ้าหน้าที่อ่านผิวเผินจะเห็นเป็น 1,079.44 + 75.56 = 1,155 แล้วสรุปว่า
 *     ค่าบริการ 105 ไม่ได้ถูกนับเข้าฐานภาษี = สำแดงภาษีขาด
 *
 * วิธีแก้: ไล่ยอดจาก "ค่าสินค้า → ส่วนลด → ค่าบริการ → รวมเป็นเงิน" ให้เห็นว่าค่าบริการ
 * ถูกรวมเข้ายอดแล้วจริง ก่อนจะแตกภาษีออกมาจากยอดรวมนั้น — บวกตามคอลัมน์แล้วลงตัวเสมอ
 */
function buildInvoiceTotalRows({ bill, items, totalAmount, vat, vatRate, t }) {
  const goodsTotal = items.reduce((sum, it) => sum + toNum(it.total), 0);
  const discount = toNum(bill.discount);
  const serviceCharge = toNum(bill.service_charge_amount);
  const shipping = toNum(bill.shipping_price);

  const rows = [{ label: t('_taxinv.Goods Total'), value: goodsTotal }];
  if (discount > 0) rows.push({ label: t('Discount'), value: -discount });
  if (serviceCharge > 0) {
    rows.push({ label: `${t('Service Charge')} (${toNum(bill.service_charge_rate)}%)`, value: serviceCharge });
  }
  if (shipping > 0) rows.push({ label: t('Shipping'), value: shipping });

  // ยอดรวมสินค้า+บริการ (รวมภาษีแล้ว) — ตัวตั้งที่ภาษีถูกถอดออกมา
  rows.push({ label: t('_taxinv.Total Before Tax Breakdown'), value: totalAmount });
  rows.push({ label: `${t('_Bill.VAT')} ${vatRate}%`, value: vat });
  rows.push({ label: t('_taxinv.Net Before VAT'), value: totalAmount - vat });
  rows.push({ label: t('_taxinv.Total Due'), value: totalAmount, bold: true });

  return rows;
}

/**
 * Build the A4 full-form tax invoice (ใบกำกับภาษี/ใบเสร็จรับเงิน) per Revenue
 * Department format. Seller header info (address / phone / logo) comes from the
 * branch_config returned by the bill print endpoint; the rest is inline on the
 * bill detail (branch_no, tax_invoice_*) plus filter.shop_tax for the seller's
 * tax id — การจด VAT เป็นของนิติบุคคล ไม่ใช่ของสถานประกอบการ.
 *
 * @param {object} opts
 * @param {object} opts.bill    GET /v1/bills/{id} `data` payload
 * @param {object} opts.config  branch_config (name, address, telephone, logo_url)
 * @param {object} opts.shopTax filter.shop_tax (tax_id, tax_entity_name)
 * @param {function} opts.fmt   currency formatter
 * @param {function} opts.t     i18n translator (default identity)
 */
export function buildTaxInvoiceA4DocDefinition({ bill, config = {}, shopTax = {}, fmt, t = (s) => s }) {
  const isFull = !!bill.tax_invoice_customer_tax_id;
  const items = groupItemsForReceipt(bill.items);

  const sellerAddr = [config.address, config.sub_district, config.district, config.province, config.postal_code]
    .filter(Boolean).join(' ');
  const sellerPhones = [config.telephone1, config.telephone2].filter(Boolean).join(' / ');

  const created = bill.tax_invoice_created_at ? new Date(bill.tax_invoice_created_at) : new Date();
  const dateText = `${created.getDate()}/${created.getMonth() + 1}/${created.getFullYear() + 543}`;

  const totalAmount = toNum(bill.tax_invoice_total_amount) || toNum(bill.total);
  const vat = toNum(bill.total_vat);
  const vatRate = toNum(bill.vat_rate);

  // ── Seller header (left) ──
  const sellerStack = [];
  if (config.logo_url) sellerStack.push({ image: config.logo_url, width: 56, margin: [0, 0, 0, 4] });
  // ชื่อผู้ประกอบการตามทะเบียน VAT ต้องมาก่อนชื่อสาขา/แบรนด์บนใบกำกับเต็มรูป
  sellerStack.push({ text: shopTax.tax_entity_name || config.name || bill.branch || '', bold: true, fontSize: 13 });
  if (sellerAddr) sellerStack.push({ text: sellerAddr, fontSize: 9, color: '#444', margin: [0, 1, 0, 0] });
  sellerStack.push({
    text: t('Tax ID') + ': ' + (shopTax.tax_id || '-')
      + (formatBranchNo(bill.branch_no, t) ? '    ' + formatBranchNo(bill.branch_no, t) : ''),
    fontSize: 9, margin: [0, 1, 0, 0],
  });
  if (sellerPhones) sellerStack.push({ text: t('Tel.') + ' ' + sellerPhones, fontSize: 9, color: '#444' });

  // ── Title + invoice meta (right) ──
  const titleStack = [
    { text: isFull ? 'ใบกำกับภาษี / ใบเสร็จรับเงิน' : 'ใบกำกับภาษีอย่างย่อ', bold: true, fontSize: 13, alignment: 'right' },
    { text: '(' + t('Original') + ')', alignment: 'right', fontSize: 9, margin: [0, 0, 0, 6] },
    {
      table: {
        widths: ['auto', '*'],
        body: [
          [{ text: t('Invoice No.') + ':', fontSize: 9, bold: true }, { text: bill.tax_invoice_no || '-', fontSize: 9, alignment: 'right' }],
          [{ text: t('Date') + ':', fontSize: 9, bold: true }, { text: dateText, fontSize: 9, alignment: 'right' }],
          [{ text: t('Bill No.') + ':', fontSize: 9, bold: true }, { text: bill.bill_no || '-', fontSize: 9, alignment: 'right' }],
        ],
      },
      layout: 'noBorders',
    },
  ];

  /**
   * กฎหมายบังคับให้ใบกำกับเต็มรูปที่ออกแทนสลิป ระบุเลขใบกำกับอย่างย่อที่มันออกแทน
   * เป็นตัวยืนยันกับสรรพากรว่าเอกสาร 2 ใบนี้คือยอดเงินก้อนเดียวกัน ไม่ได้ขายซ้ำ
   * (silver-spoon-api/docs/vat-compliance.md D9 — ใบย่อคือเอกสารแม่ ใบเต็มรูปออกแทนทีหลัง)
   */
  if (bill.abbreviated_tax_invoice_no) {
    titleStack.push({
      text: t('Issued in place of abbreviated tax invoice no.') + ' ' + bill.abbreviated_tax_invoice_no,
      fontSize: 8,
      alignment: 'right',
      margin: [0, 4, 0, 0],
    });
  }

  // ── Customer box ──
  const customerBox = {
    table: {
      widths: ['auto', '*'],
      body: [
        [{ text: t('Customer') + ':', bold: true, fontSize: 10 }, { text: bill.tax_invoice_customer_name || '-', fontSize: 10 }],
        /**
         * ที่อยู่ผู้ซื้อ — ม.86/4(3) บังคับ ("ชื่อ ที่อยู่ และเลขประจำตัวผู้เสียภาษีของผู้ซื้อ")
         * ระบบเก็บไว้ตั้งแต่ตอนออกใบแล้ว แต่เดิมไม่เคยพิมพ์ลงเอกสาร
         * ไม่มีค่าก็ยังต้องขึ้นบรรทัดพร้อมขีด เพื่อให้เห็นว่าช่องนี้ยังไม่ได้กรอก
         */
        [
          { text: t('Address') + ':', bold: true, fontSize: 10 },
          { text: bill.tax_invoice_customer_address || '-', fontSize: 10 },
        ],
        [
          { text: t('Tax ID') + ':', bold: true, fontSize: 10 },
          {
            text: (bill.tax_invoice_customer_tax_id || '-')
              + (bill.tax_invoice_customer_branch ? '    ' + t('Branch No') + ': ' + bill.tax_invoice_customer_branch : ''),
            fontSize: 10,
          },
        ],
      ],
    },
    layout: {
      hLineColor: () => '#bbb', vLineColor: () => '#bbb',
      paddingLeft: () => 6, paddingRight: () => 6, paddingTop: () => 3, paddingBottom: () => 3,
    },
    margin: [0, 10, 0, 10],
  };

  // ── Items table ──
  const itemsBody = [
    [
      { text: '#', style: 'thInv', alignment: 'center' },
      { text: t('Description'), style: 'thInv' },
      { text: t('Qty'), style: 'thInv', alignment: 'center' },
      { text: t('Unit Price'), style: 'thInv', alignment: 'right' },
      // ใช้คีย์ module `_Bill.Amount` = "จำนวนเงิน" ไม่ใช่ `Amount` ที่แปลว่า "จำนวน"
      // ซึ่งซ้ำกับหัวคอลัมน์ Qty พอดี ทำให้ตารางมีหัว "จำนวน" 2 ช่อง
      { text: t('_Bill.Amount'), style: 'thInv', alignment: 'right' },
    ],
  ];
  items.forEach((it, idx) => {
    itemsBody.push([
      { text: String(idx + 1), fontSize: 9, alignment: 'center' },
      { text: t(it.name) + (it.is_reward ? ' (' + t('Reward') + ')' : ''), fontSize: 9 },
      { text: String(it.quantity), fontSize: 9, alignment: 'center' },
      { text: fmt(it.unit_price), fontSize: 9, alignment: 'right' },
      { text: fmt(it.total), fontSize: 9, alignment: 'right' },
    ]);
  });
  // Pad to a minimum number of rows so the form keeps a consistent height.
  const minRows = 8;
  for (let i = items.length; i < minRows; i++) {
    itemsBody.push([{ text: ' ', fontSize: 9 }, {}, {}, {}, {}]);
  }

  const totalsRows = [];
  const totalRow = (label, value, bold = false) => [
    { text: '', border: [false, false, false, false] },
    { text: '', border: [false, false, false, false] },
    { text: '', border: [false, false, false, false] },
    { text: label, fontSize: 9, bold, alignment: 'right' },
    { text: fmt(value), fontSize: 9, bold, alignment: 'right' },
  ];
  buildInvoiceTotalRows({ bill, items, totalAmount, vat, vatRate, t })
    .forEach((r) => totalsRows.push(totalRow(r.label, r.value, !!r.bold)));

  return {
    pageSize: 'A4',
    pageMargins: [32, 32, 32, 36],
    defaultStyle: { font: 'Sarabun', fontSize: 10 },
    styles: {
      thInv: { bold: true, fontSize: 9, fillColor: '#f0f0f0' },
    },
    content: [
      {
        columns: [
          { width: '60%', stack: sellerStack },
          { width: '40%', stack: titleStack },
        ],
      },
      customerBox,
      {
        table: {
          headerRows: 1,
          widths: [20, '*', 36, 70, 80],
          body: itemsBody,
        },
        layout: {
          hLineColor: () => '#ccc', vLineColor: () => '#ccc',
          paddingTop: () => 3, paddingBottom: () => 3,
        },
      },
      {
        table: {
          widths: ['*', 'auto', 'auto', 'auto', 'auto'],
          body: totalsRows,
        },
        layout: 'noBorders',
        margin: [0, 4, 0, 8],
      },
      {
        table: {
          widths: ['*'],
          body: [[{ text: '(' + bahtText(totalAmount) + ')', alignment: 'center', bold: true, fontSize: 10, margin: [0, 3, 0, 3] }]],
        },
        layout: {
          hLineColor: () => '#bbb', vLineColor: () => '#bbb',
        },
        margin: [0, 0, 0, 24],
      },
      {
        columns: [
          {
            width: '*',
            stack: [
              { text: '...........................................', alignment: 'center', fontSize: 9 },
              { text: t('Customer / Receiver'), alignment: 'center', fontSize: 9, color: '#444' },
            ],
          },
          {
            width: '*',
            stack: [
              { text: '...........................................', alignment: 'center', fontSize: 9 },
              { text: t('Authorized Signature'), alignment: 'center', fontSize: 9, color: '#444' },
            ],
          },
        ],
      },
    ],
  };
}

export { printPdf };
